import { Link } from "react-router-dom";
import { ArrowUpRight, Check, X } from "lucide-react";
import { Eyebrow, MaskedLine, Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { SEO } from "../components/SEO";
import { useContent } from "../lib/content";

export default function Services() {
  const { content } = useContent();
  const services = content.services;

  return (
    <div data-testid="services-page">
      <SEO
        title="Bookkeeping, Payroll Oversight & Advisory Services | Rooted Business Advisors"
        description="Monthly bookkeeping, financial reporting, payroll oversight, compensation consulting, business advisory and special projects for small businesses."
      />
      <section className="bg-forest-deep pb-24 pt-44 text-cream">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Eyebrow light>Our Services</Eyebrow>
          <h1 className="mt-8 font-display text-5xl font-medium leading-[0.98] tracking-tight sm:text-6xl lg:text-7xl" data-testid="services-headline">
            <MaskedLine delay={0.15}>Every Branch of Your</MaskedLine>
            <MaskedLine delay={0.3} className="italic text-gold">
              Finances, Tended To.
            </MaskedLine>
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-8 max-w-xl leading-relaxed text-cream/75 md:text-lg">
              From day-to-day bookkeeping to long-range advisory, each
              engagement is scoped with total clarity — you'll always know
              exactly what's included, and what isn't.
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        {services.map((s, i) => (
          <Reveal key={s.id || i} delay={0.05}>
            <article
              id={s.id || `service-${i}`}
              className="grid scroll-mt-32 gap-10 border-t border-forest/15 py-16 last:border-b lg:grid-cols-12"
              data-testid={`service-chapter-${s.id || i}`}
            >
              <div className="lg:col-span-5">
                <span className="font-display text-5xl italic text-gold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-forest md:text-4xl">
                  {s.name}
                </h2>
                <p className="mt-5 max-w-md leading-relaxed text-sage">{s.blurb}</p>
                <Link
                  to={`/contact?service=${encodeURIComponent(s.name)}`}
                  data-testid={`service-cta-${s.id || i}`}
                  className="group mt-8 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-forest"
                >
                  <span className="link-underline">Ask About This Service</span>
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>

              <div className="grid gap-10 sm:grid-cols-2 lg:col-span-7">
                <div className="border border-forest/15 bg-cream p-8">
                  <h3 className="text-[11px] font-semibold uppercase tracking-[0.24em] text-olive">
                    What's Included
                  </h3>
                  <ul className="mt-6 space-y-3.5">
                    {(s.included || []).map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-forest/85">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-olive" strokeWidth={2} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                {(s.excluded || []).length > 0 && (
                  <div className="border border-forest/10 bg-parchment p-8">
                    <h3 className="text-[11px] font-semibold uppercase tracking-[0.24em] text-sage">
                      What's Not Included
                    </h3>
                    <ul className="mt-6 space-y-3.5">
                      {s.excluded.map((item) => (
                        <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-sage">
                          <X className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={2} />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </article>
          </Reveal>
        ))}
      </section>

      <section className="bg-forest text-cream" data-testid="services-cta-section">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 py-20 md:flex-row md:items-center lg:px-10">
          <Reveal>
            <h2 className="max-w-xl font-display text-3xl font-medium leading-tight tracking-tight md:text-4xl">
              Not sure which service fits?{" "}
              <span className="italic text-gold">Let's figure it out together.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/contact"
              data-testid="services-cta-button"
              className="group inline-flex items-center gap-2 rounded-full bg-cream px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-forest transition-colors duration-300 hover:bg-gold"
            >
              Request a Consultation
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
